import PropTypes from 'prop-types';
import moment from 'moment';
import { capitalize, isEmpty, filter, startCase, last } from 'lodash';
import { Alert } from 'react-bootstrap/lib';
import React, { PureComponent } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { ImportStatus } from '@upstart/harmony/lib/constants';
import { getValidationClassName } from '../../util/utils';
import './DealerList.scss';
import EditButton from './EditButton';
import AddButton from './AddButton';
import ReactivateButton from './ReactivateButton';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';
import ValidateModal from './ValidateModal';
import ImportDealerRunsModal from './ImportDealerRunsModal';

export const getImportDealerEventClassName = (importDealerEventType) => {
  switch (importDealerEventType) {
    case ImportStatus.IMPORT_FINISHED:
      return 'status-success';
    case ImportStatus.IMPORT_TIMEOUT:
    case ImportStatus.IMPORT_ERROR:
      return 'status-error';
    case ImportStatus.IMPORT_RUNNING:
      return 'status-running';
    default:
      return '';
  }
};

class DealerList extends PureComponent {
  constructor() {
    super();
    this.state = {
      dealerVerification: null,
    };
  }

  actionFormatter = (cell, dealer) => (
    <EditButton
      dealer={dealer}
      editDealer={this.props.editDealer}
      onDelete={this.props.onDelete}
      onValidate={this.props.onValidate}
      runAutomation={this.props.runAutomation}
      onCancel={this.props.onCancel}
      onInventoryImport={this.props.onInventoryImport}
    />
  );

  openStatusModal = (dealerVerification) => {
    this.setState({ dealerVerification });
  };

  closeStatusModal = () => {
    this.setState({ dealerVerification: null });
  };

  statusFormatter = (
    cell,
    { dealerVerification, importDealerRuns, ...dealer },
  ) => {
    if (
      !dealerVerification &&
      (!importDealerRuns || isEmpty(importDealerRuns))
    ) {
      return (
        <button
          className="btn btn-secondary"
          disabled={this.props.fetching}
          onClick={() => this.props.onAddExtraInformation(dealer.id)}
        >
          Load Info
        </button>
      );
    }
    return dealerVerification && !!dealerVerification.status ? (
      <div className="validation-status-container">
        <span className={getValidationClassName(dealerVerification.status)}>
          {dealerVerification.status}
        </span>
        <small>[{capitalize(dealerVerification.type)} Tests]</small>
        <small>
          {moment(dealerVerification.updatedAt).format('MM-DD-YYYY hh:mm')}
        </small>
        <small>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={() => this.openStatusModal(dealerVerification)}>
            detail
          </a>
        </small>
      </div>
    ) : (
      <span>n/a</span>
    );
  };

  importDealerRunsFormatter = (cell, { importDealerRuns, ...dealer }) => {
    if (!importDealerRuns || isEmpty(importDealerRuns)) {
      return (
        <button
          className="btn btn-secondary"
          onClick={() => this.props.onAddExtraInformation(dealer.id)}
          disabled={this.props.fetching}
        >
          Load Info
        </button>
      );
    }
    if (isEmpty(importDealerRuns)) {
      return <span>No import recorded yet</span>;
    }
    const lastImportDealerRun = importDealerRuns[0];
    const lastEvent = last(lastImportDealerRun.events);
    return (
      <div className="validation-status-container">
        <span className={getImportDealerEventClassName(lastEvent.eventType)}>
          {startCase(lastEvent.eventType)}
        </span>
        <small>{moment(lastEvent.timestamp).format('MM-DD-YYYY HH:mm')}</small>
        <small>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            onClick={() => this.importDealerRunsModal.open(lastImportDealerRun)}
          >
            stats
          </a>
        </small>
      </div>
    );
  };

  dealerGroupFormatter = (dealerGroup) => (
    <Link to={`group/${dealerGroup}`}>
      {this.props.groupsMap[dealerGroup] || dealerGroup}
    </Link>
  );

  getGroupName = (_, row) =>
    row.dealergroup_id ? this.props.groupsMap[row.dealergroup_id] : null;

  createCustomInsertButton = () => (
    <div className="dealer-list-insert-buttons">
      <AddButton />
      <ReactivateButton />
    </div>
  );

  render() {
    const { list, error, success, fetching } = this.props;
    const options = {
      insertBtn: this.createCustomInsertButton,
      defaultSortName: 'name',
      defaultSortOrder: 'asc',
    };

    return (
      <div className="dealer-list-container">
        {fetching && <Alert>Loading...</Alert>}
        {error && <ErrorMessage error={error} />}
        {success && <SuccessMessage success={success} />}
        <BootstrapTable
          data={filter(
            list,
            ({ isDealerGroupConfigHolder }) => !isDealerGroupConfigHolder,
          )}
          options={options}
          insertRow={!fetching}
          striped
          hover
          pagination
          bodyContainerClass="list-body-container"
          tableStyle={{ border: 'none' }}
        >
          <TableHeaderColumn dataField="id">ID</TableHeaderColumn>
          <TableHeaderColumn
            isKey
            dataField="name"
            dataSort
            filter={{ type: 'TextFilter', defaultValue: '' }}
          >
            Name
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="dealergroup_id"
            dataFormat={this.dealerGroupFormatter}
            dataSort
            filter={{ type: 'TextFilter', defaultValue: '' }}
            filterValue={this.getGroupName}
          >
            Group
          </TableHeaderColumn>
          <TableHeaderColumn dataField="crmType" dataSort>
            CRM Type
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="button"
            dataFormat={this.importDealerRunsFormatter}
            dataSort
          >
            Import Status
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="button"
            dataFormat={this.statusFormatter}
            dataSort
          >
            Status
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="button"
            columnClassName="list-action-column"
            dataFormat={this.actionFormatter}
          />
        </BootstrapTable>
        <ValidateModal
          dealerVerification={this.state.dealerVerification}
          onClose={this.closeStatusModal}
        />
        <ImportDealerRunsModal
          ref={(ref) => {
            this.importDealerRunsModal = ref;
          }}
        />
      </div>
    );
  }
}

DealerList.propTypes = {
  error: PropTypes.any,
  success: PropTypes.any,
  list: PropTypes.array,
  fetching: PropTypes.bool,
  onDelete: PropTypes.func,
  onValidate: PropTypes.func,
  runAutomation: PropTypes.func,
  onCancel: PropTypes.func,
  onInventoryImport: PropTypes.func,
  editDealer: PropTypes.func,
  groupsMap: PropTypes.object,
  onAddExtraInformation: PropTypes.func,
};

export default withRouter(DealerList);
